import { useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useBundleTreeStore } from '@/components/bundle/bundleTreeStore';
import { Button } from '@/components/ui/NewButton';
import { apiErrorMessage } from '@/lib/api-client';
import { useUiChangeSignal } from '@/lib/uiChangeBus';
import type { BundleResponse } from '@/hooks/useBundle';

export function hydrateBundleTree(matterGuid: string, bundle: BundleResponse) {
  useBundleTreeStore.getState().actions.initialize({
    matterGuid,
    initialGroups: bundle.groups,
    initialEmailAttachments: bundle.email_attachments,
    title: bundle.title,
    description: bundle.description,
    metadata: bundle.metadata,
  });
}

type UseBundleRemoteRefreshOptions = {
  bundleId: string;
  matterGuid: string;
  mutate: () => Promise<BundleResponse | undefined>;
  resourceNoun: string;
  /** When set, a dirty editor is never overwritten — a Reload toast is shown instead. */
  isDirty?: () => boolean;
};

/**
 * Reload an open bundle viewer/editor when Case Partner (or anything else)
 * publishes a `bundle` UI change for this id.
 */
export function useBundleRemoteRefresh({
  bundleId,
  matterGuid,
  mutate,
  resourceNoun,
  isDirty,
}: UseBundleRemoteRefreshOptions) {
  const changeSignal = useUiChangeSignal('bundle', bundleId);

  // Only the signal should retrigger; mutate/apply are the current closures.
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh is driven by changeSignal only
  useEffect(() => {
    if (changeSignal === 0) return;
    let cancelled = false;
    const shouldApply = () => !cancelled;

    const reload = async (skipIfDirty: boolean) => {
      try {
        const fresh = await mutate();
        if (!fresh || !shouldApply()) return { applied: false, becameDirty: false };
        if (skipIfDirty && isDirty?.()) {
          return { applied: false, becameDirty: true };
        }
        hydrateBundleTree(matterGuid, fresh);
        return { applied: true, becameDirty: false };
      } catch (err: unknown) {
        if (!cancelled) {
          toast.error(apiErrorMessage(err) || `Failed to refresh this ${resourceNoun}.`);
        }
        return { applied: false, becameDirty: false };
      }
    };

    const showDirtyToast = () => {
      toast(
        (t) => (
          <div className="flex items-center gap-3">
            <span className="text-sm">
              Case Partner updated this {resourceNoun}. Your unsaved changes are still here — reload to use its version.
            </span>
            <Button
              size="sm"
              text="Reload"
              onClick={() => {
                toast.dismiss(t.id);
                void reload(false);
              }}
            />
          </div>
        ),
        { duration: 12000 },
      );
    };

    if (!isDirty) {
      void reload(false);
      return () => {
        cancelled = true;
      };
    }

    if (!isDirty()) {
      void reload(true).then((result) => {
        if (!cancelled && result.becameDirty) showDirtyToast();
      });
    } else {
      showDirtyToast();
    }

    return () => {
      cancelled = true;
    };
  }, [changeSignal]);
}
